/**
 * Tabulka evidence RD (Rodinné domy) — field resolvers + missing-data red flags.
 * Used by web Excel export and BE workbook builder.
 */

import {
  formatBabovkaMaterialRdExportLabel,
  hasRequiredBabovkaMaterial,
  resolveBabovkaRequiredMaterialSap,
} from '../constants/object-workflow/babovka-material.constants';
import {
  isBabovkaPlacement,
  type BabovkaPlacement,
} from '../constants/object-workflow/babovka-placement.constants';
import {
  formatUkonceniVRdLabel,
  isUkonceniVRdSelectionValid,
} from '../constants/object-workflow/ukonceni-v-rd.constants';
import {
  isDigitalniCestaPathSpecValid,
  normalizeDigitalniCestaSpec,
  resizeDigitalniCestaSpec,
  type DigitalniCestaPathSpec,
} from '../constants/object-workflow/digitalni-cesta-spec.constants';
import { fiberCountFromTypOk } from '../constants/object-workflow/typ-ok.constants';
import type { OkMaterialUsageLike } from '../constants/object-workflow/typ-ok-from-material.constants';
import {
  inferBudovaBuildingType,
  parseBudovaMilestonesFromMetadata,
} from '../constants/object-workflow/budova-milestone-engine';
import {
  resolveBudovaRdAddressFields,
  toBudovaAcquisitionAddress,
  type BudovaRdAddressFields,
} from './budova-address-label.util';
import {
  TYP_CISLA_EVIDENCNI,
  TYP_CISLA_POPISNE,
  typCislaOrDefault,
  type TypCisla,
} from './typ-cisla.util';

export type { BudovaRdAddressFields };

/**
 * Map a typical BUDOVA list/API payload onto coalesced RD address fields
 * (`address_points` → ACQ → `objects.parcel_number`).
 */
export function resolveRdAddressFieldsFromBudova(obj: {
  addressPoint?: {
    cisloPopisne?: string | null;
    typCisla?: TypCisla | string | null;
    orientacni?: string | null;
    ulice?: string | null;
    obec?: string | null;
  } | null;
  acquisition?: unknown;
  parcelNumber?: string | null;
} | null | undefined): BudovaRdAddressFields {
  const acq =
    obj?.acquisition && typeof obj.acquisition === 'object'
      ? (obj.acquisition as Record<string, unknown>)
      : null;
  return resolveBudovaRdAddressFields({
    addressPoint: obj?.addressPoint ?? null,
    acquisitionAddress: toBudovaAcquisitionAddress(obj?.acquisition),
    parcelNumber: obj?.parcelNumber ?? null,
    acquisitionRawData:
      acq && acq['rawData'] && typeof acq['rawData'] === 'object'
        ? (acq['rawData'] as Record<string, unknown>)
        : null,
  });
}

/** Light red fill for missing RD cells (ARGB) — same tone as WF exclusion. */
export const RD_MISSING_DATA_FILL_ARGB = 'FFFFCDD2';

/**
 * Canonical header labels in export column order.
 * House number is split: č.p. vs č.ev. (RÚIAN `typ_cisla`); parcel is separate.
 *
 * Ukončení group: v RD umístění → digitální cesty → kryt OK (material) →
 * před RD umístění (bábovka land). Deleted: „Ukončení před RD Ukončení MT s ok“.
 */
export const RD_TABLE_EXPORT_HEADERS = [
  'KIA kód',
  'Město',
  'Ulice',
  'Číslo popisné',
  'Číslo evidenční',
  'Číslo parcelní',
  'ROP ID',
  'ukončení v RD QR - kód',
  'Ukončení v RD umístění',
  'Ukončení digitální cesty umístění',
  'počet vláken ponechaných v rezervě',
  'Ukončení před RD kryt OK',
  'Ukončení před RD umístění',
  'foto rezervy ok',
  'ukončení před RD souřasnice_X',
  'ukončení před RD souřasnice_Y',
] as const;

export type RdTableExportHeader = (typeof RD_TABLE_EXPORT_HEADERS)[number];

/** Normalized header keys used by resolvers / red-flag map. */
export type RdExportHeaderKey =
  | 'kia_kod'
  | 'mesto'
  | 'ulice'
  | 'cislo_popisne'
  | 'cislo_evidencni'
  | 'cislo_parcelni'
  | 'rop_id'
  | 'ukonceni_v_rd_qr'
  | 'ukonceni_v_rd_umisteni'
  | 'ukonceni_digitalni_cesty_umisteni'
  | 'pocet_vlaken_rezerva'
  | 'ukonceni_pred_rd_kryt_ok'
  | 'ukonceni_pred_rd_umisteni'
  | 'foto_rezervy_ok'
  | 'ukonceni_pred_rd_x'
  | 'ukonceni_pred_rd_y'
  | 'unknown';

/**
 * „Ukončení před RD umístění“ — bábovka land placement (never material / fiber).
 */
export const BABOVKA_PLACEMENT_RD_LAND_LABELS: Readonly<Record<BabovkaPlacement, string>> =
  Object.freeze({
    PRED_OBJEKTEM: 'Před pozemkem zákazníka',
    V_OBJEKTU: 'V pozemku zákazníka',
  });

export type RdExportCellValue = string | number | boolean | null | undefined;

export interface RdExportObjectInput {
  kiaCode?: string | null;
  mesto?: string | null;
  ulice?: string | null;
  /**
   * Domovní číslo (digits) for both č.p. and č.ev. — type is {@link typCisla}.
   * Historical name matches `address_points.cislo_popisne`.
   */
  cisloPopisne?: string | null;
  /** `P` = číslo popisné, `E` = číslo evidenční (RÚIAN / KN cp_ce). */
  typCisla?: TypCisla | string | null;
  /** Orientační číslo — shown only with číslo popisné (e.g. `23/5`). */
  orientacni?: string | null;
  /** Parcelní číslo budovy (`objects.parcel_number` / ACQ). */
  parcelNumber?: string | null;
  ropId?: string | null;
  /** QR / legacy raw for „ukončení v RD QR - kód“. */
  ukonceniVRdQr?: string | null;
  ukonceniVRd?: string | null;
  ukonceniVRdOther?: string | null;
  zafukTypOk?: string | null;
  zafukLengthM?: number | string | null;
  zafukZRozvadecObjectId?: string | null;
  zafukSourceObjectId?: string | null;
  digitalniCesta?: number | null;
  digitalniCestaSpec?: DigitalniCestaPathSpec[] | null;
  babovkaPlacement?: string | null;
  /** Material usage rows (any phase) — filtered to 327065/327067 in resolvers. */
  materials?: readonly OkMaterialUsageLike[] | null;
  budovaBuildingType?: string | null;
  status?: string | null;
  metadata?: Record<string, unknown> | null;
  photoCount?: number | null;
  sjtskX?: string | null;
  sjtskY?: string | null;
  /**
   * Legacy acquisition raw values keyed by {@link normalizeRdExportHeader}.
   * Used only as fallback for QR / foto when domain fields are empty.
   */
  rawByHeaderNorm?: Record<string, RdExportCellValue>;
}

export interface RdExportMissingFlags {
  /** záfuk exists but typ OK / fiber count missing → red „počet vláken ponechaných v rezervě“. */
  missingZafukOk: boolean;
  /** digitální cesta count &gt; 0 but path specs incomplete → red digitální cesty umístění. */
  missingDigitalniCestaSpec: boolean;
  /** bábovka without 327065/327067 → red „kryt OK“. */
  missingBabovkaMaterial: boolean;
  /** bábovka without placement → red „Ukončení před RD umístění“. */
  missingBabovkaPlacement: boolean;
  /** závrt without ukončení v RD → red „Ukončení v RD umístění“. */
  missingZavrtUkonceni: boolean;
}

/** Normalize Excel header text for matching (strip diacritics, collapse space). */
export function normalizeRdExportHeader(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

export function resolveRdExportHeaderKey(header: string | null | undefined): RdExportHeaderKey {
  const norm = normalizeRdExportHeader(header);
  if (!norm) return 'unknown';
  if (norm === 'kia kod' || norm === 'kia kód') return 'kia_kod';
  if (norm === 'mesto') return 'mesto';
  if (norm === 'ulice') return 'ulice';
  if (norm === 'cislo popisne') return 'cislo_popisne';
  if (norm === 'cislo evidencni') return 'cislo_evidencni';
  if (norm === 'cislo parcelni' || norm === 'parcelni cislo') return 'cislo_parcelni';
  if (norm === 'rop id') return 'rop_id';
  if (norm === 'ukonceni v rd qr - kod') return 'ukonceni_v_rd_qr';
  if (norm === 'ukonceni v rd umisteni') return 'ukonceni_v_rd_umisteni';
  if (norm === 'ukonceni digitalni cesty umisteni') return 'ukonceni_digitalni_cesty_umisteni';
  // More specific „ukončení před RD …“ aliases before the short „ukončení před RD“.
  if (norm === 'ukonceni pred rd kryt ok') return 'ukonceni_pred_rd_kryt_ok';
  if (norm === 'ukonceni pred rd sourasnice_x') return 'ukonceni_pred_rd_x';
  if (norm === 'ukonceni pred rd sourasnice_y') return 'ukonceni_pred_rd_y';
  if (
    norm === 'ukonceni pred rd umisteni' ||
    norm === 'ukonceni pred rd'
  ) {
    return 'ukonceni_pred_rd_umisteni';
  }
  if (
    norm === 'pocet vlaken ponechanych v rezerve' ||
    norm === 'pocet vlaken' ||
    norm === 'pocet vlaken v rezerve'
  ) {
    return 'pocet_vlaken_rezerva';
  }
  if (norm === 'foto rezervy ok') return 'foto_rezervy_ok';
  return 'unknown';
}

/** Domovní číslo (+ orientační) bez prefixu č.p./č.ev. */
export function formatRdHouseNumberDigits(
  cisloPopisne: string | null | undefined,
  orientacni?: string | null,
): string {
  const cp = (cisloPopisne ?? '').trim();
  if (!cp) return '';
  const orient = (orientacni ?? '').trim();
  return orient ? `${cp}/${orient}` : cp;
}

/** Value for „Číslo popisné“ — only when `typ_cisla` is popisné. */
export function resolveRdCisloPopisne(input: RdExportObjectInput): string {
  if (typCislaOrDefault(input.typCisla) !== TYP_CISLA_POPISNE) return '';
  return formatRdHouseNumberDigits(input.cisloPopisne, input.orientacni);
}

/** Value for „Číslo evidenční“ — only when `typ_cisla` is evidenční (no orient). */
export function resolveRdCisloEvidencni(input: RdExportObjectInput): string {
  if (typCislaOrDefault(input.typCisla) !== TYP_CISLA_EVIDENCNI) return '';
  return (input.cisloPopisne ?? '').trim();
}

export function resolveRdCisloParcelni(input: RdExportObjectInput): string {
  return (input.parcelNumber ?? '').trim();
}

function nonEmpty(value: RdExportCellValue): RdExportCellValue {
  if (value == null) return '';
  if (typeof value === 'string' && value.trim() === '') return '';
  return value;
}

function parseLength(value: number | string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function rawFallback(input: RdExportObjectInput, headerNorm: string): RdExportCellValue {
  const v = input.rawByHeaderNorm?.[headerNorm];
  return v == null ? '' : v;
}

export function formatBabovkaPlacementRdLandLabel(
  placement: string | null | undefined,
): string {
  const key = (placement ?? '').trim();
  if (isBabovkaPlacement(key)) return BABOVKA_PLACEMENT_RD_LAND_LABELS[key];
  return '';
}

/**
 * „Ukončení před RD kryt OK“ for bábovka: short material cover label only.
 * Example: `Kruhový kryt` / `Komora malá`
 */
export function formatBabovkaKrytOkRdExportLabel(
  materials: readonly OkMaterialUsageLike[] | null | undefined,
): string {
  const sap = resolveBabovkaRequiredMaterialSap(materials);
  return formatBabovkaMaterialRdExportLabel(sap);
}

/** One path: `1. NP - Obytný prostor`; multi joined with `, `. */
export function formatDigitalniCestaUmistenniRdExportLabel(
  count: number | null | undefined,
  specs: DigitalniCestaPathSpec[] | null | undefined,
): string {
  const n = Math.max(0, Math.trunc(Number(count ?? 0)) || 0);
  if (n <= 0) return '';
  const list = resizeDigitalniCestaSpec(specs, n);
  const parts: string[] = [];
  for (const item of list) {
    const patro = (item.patro ?? '').trim();
    const uk = formatUkonceniVRdLabel(item.ukonceni, item.ukonceniOther);
    if (!patro && !uk) continue;
    if (patro && uk) parts.push(`${patro} - ${uk}`);
    else parts.push(patro || uk);
  }
  return parts.join(', ');
}

export function rdExportHasZafuk(input: RdExportObjectInput): boolean {
  const length = parseLength(input.zafukLengthM);
  if (length != null && length > 0) return true;
  if ((input.zafukTypOk ?? '').trim()) return true;
  if ((input.zafukZRozvadecObjectId ?? '').trim()) return true;
  if ((input.zafukSourceObjectId ?? '').trim()) return true;
  const flags = parseBudovaMilestonesFromMetadata(input.metadata ?? undefined);
  if (flags.zafouknuto) return true;
  return false;
}

export function rdExportIsBabovka(input: RdExportObjectInput): boolean {
  const type =
    (input.budovaBuildingType ?? '').trim().toUpperCase() ||
    inferBudovaBuildingType({
      explicitType: input.budovaBuildingType,
      status: input.status,
      metadata: input.metadata ?? undefined,
    }) ||
    '';
  if (type === 'BABOVKA') return true;
  const flags = parseBudovaMilestonesFromMetadata(input.metadata ?? undefined);
  return flags.postavenaBabovka === true;
}

export function rdExportHasZavrt(input: RdExportObjectInput): boolean {
  const flags = parseBudovaMilestonesFromMetadata(input.metadata ?? undefined);
  return flags.zavrtany === true;
}

export function resolveRdExportMissingFlags(input: RdExportObjectInput): RdExportMissingFlags {
  const missingZafukOk =
    rdExportHasZafuk(input) && !(input.zafukTypOk ?? '').trim();

  const digiCount = Math.max(0, Math.trunc(Number(input.digitalniCesta ?? 0)) || 0);
  let missingDigitalniCestaSpec = false;
  if (digiCount > 0) {
    const list = resizeDigitalniCestaSpec(input.digitalniCestaSpec, digiCount);
    missingDigitalniCestaSpec = !list.every((item) => isDigitalniCestaPathSpecValid(item));
  }

  const isBabovka = rdExportIsBabovka(input);
  const missingBabovkaMaterial = isBabovka && !hasRequiredBabovkaMaterial(input.materials);
  const missingBabovkaPlacement =
    isBabovka && !isBabovkaPlacement((input.babovkaPlacement ?? '').trim());

  const ukonceniOk = isUkonceniVRdSelectionValid(input.ukonceniVRd, input.ukonceniVRdOther);
  const missingZavrtUkonceni = rdExportHasZavrt(input) && !ukonceniOk;

  return {
    missingZafukOk,
    missingDigitalniCestaSpec,
    missingBabovkaMaterial,
    missingBabovkaPlacement,
    missingZavrtUkonceni,
  };
}

/**
 * Which cells in a row should get red fill (empty value).
 * Keys are {@link RdExportHeaderKey}.
 */
export function resolveRdExportRedHeaderKeys(
  input: RdExportObjectInput,
): ReadonlySet<RdExportHeaderKey> {
  const flags = resolveRdExportMissingFlags(input);
  const out = new Set<RdExportHeaderKey>();
  if (flags.missingZafukOk) out.add('pocet_vlaken_rezerva');
  if (flags.missingDigitalniCestaSpec) out.add('ukonceni_digitalni_cesty_umisteni');
  if (flags.missingBabovkaMaterial) out.add('ukonceni_pred_rd_kryt_ok');
  if (flags.missingBabovkaPlacement) out.add('ukonceni_pred_rd_umisteni');
  if (flags.missingZavrtUkonceni) out.add('ukonceni_v_rd_umisteni');
  return out;
}

export function resolveRdFiberReserveValue(input: RdExportObjectInput): RdExportCellValue {
  const fibers = fiberCountFromTypOk(input.zafukTypOk);
  if (fibers != null) return fibers;
  return '';
}

export function resolveRdUkonceniVRdUmisteni(input: RdExportObjectInput): string {
  return formatUkonceniVRdLabel(input.ukonceniVRd, input.ukonceniVRdOther);
}

export function resolveRdDigitalniCestaUmisteni(input: RdExportObjectInput): string {
  return formatDigitalniCestaUmistenniRdExportLabel(
    input.digitalniCesta,
    normalizeDigitalniCestaSpec(input.digitalniCestaSpec ?? []),
  );
}

export function resolveRdBabovkaKrytOk(input: RdExportObjectInput): string {
  if (!rdExportIsBabovka(input)) {
    return String(rawFallback(input, 'ukonceni pred rd kryt ok') || '');
  }
  const flags = resolveRdExportMissingFlags(input);
  if (flags.missingBabovkaMaterial) return '';
  return formatBabovkaKrytOkRdExportLabel(input.materials);
}

/**
 * „Ukončení před RD umístění“ (also short header „ukončení před RD“):
 * bábovka land placement only. Cover material lives in „kryt OK“.
 */
export function resolveRdUkonceniPredRdUmisteni(input: RdExportObjectInput): string {
  if (rdExportIsBabovka(input)) {
    return formatBabovkaPlacementRdLandLabel(input.babovkaPlacement);
  }
  return String(rawFallback(input, 'ukonceni pred rd umisteni') || '').trim();
}

/** Resolve one cell by normalized header (template-driven export). */
export function resolveRdExportCellValue(
  input: RdExportObjectInput,
  header: string,
): RdExportCellValue {
  const key = resolveRdExportHeaderKey(header);
  const red = resolveRdExportRedHeaderKeys(input);
  if (red.has(key)) return '';

  switch (key) {
    case 'kia_kod':
      return nonEmpty(input.kiaCode ?? '');
    case 'mesto':
      return nonEmpty(input.mesto ?? '');
    case 'ulice':
      return nonEmpty(input.ulice ?? '');
    case 'cislo_popisne':
      return nonEmpty(resolveRdCisloPopisne(input));
    case 'cislo_evidencni':
      return nonEmpty(resolveRdCisloEvidencni(input));
    case 'cislo_parcelni':
      return nonEmpty(resolveRdCisloParcelni(input));
    case 'rop_id':
      return nonEmpty(input.ropId ?? '');
    case 'ukonceni_v_rd_qr':
      return nonEmpty(
        input.ukonceniVRdQr ?? rawFallback(input, normalizeRdExportHeader(header)),
      );
    case 'ukonceni_v_rd_umisteni': {
      const fromSpec = resolveRdUkonceniVRdUmisteni(input);
      if (fromSpec) return fromSpec;
      return nonEmpty(rawFallback(input, 'ukonceni v rd umisteni'));
    }
    case 'ukonceni_digitalni_cesty_umisteni':
      return nonEmpty(resolveRdDigitalniCestaUmisteni(input));
    case 'pocet_vlaken_rezerva':
      return nonEmpty(resolveRdFiberReserveValue(input));
    case 'ukonceni_pred_rd_kryt_ok':
      return nonEmpty(resolveRdBabovkaKrytOk(input));
    case 'ukonceni_pred_rd_umisteni':
      return nonEmpty(resolveRdUkonceniPredRdUmisteni(input));
    case 'foto_rezervy_ok': {
      if (typeof input.photoCount === 'number' && input.photoCount > 0) return 'Ano';
      return nonEmpty(rawFallback(input, 'foto rezervy ok'));
    }
    case 'ukonceni_pred_rd_x':
      return nonEmpty(input.sjtskX ?? '');
    case 'ukonceni_pred_rd_y':
      return nonEmpty(input.sjtskY ?? '');
    default:
      return nonEmpty(rawFallback(input, normalizeRdExportHeader(header)));
  }
}

/** Build a full data row for the given header list (template or {@link RD_TABLE_EXPORT_HEADERS}). */
export function buildRdExportRow(
  input: RdExportObjectInput,
  headers: readonly string[],
): RdExportCellValue[] {
  return headers.map((h) => resolveRdExportCellValue(input, h));
}

/** Column indexes (0-based) that should be red-filled for this row. */
export function resolveRdExportRedColumnIndexes(
  input: RdExportObjectInput,
  headers: readonly string[],
): number[] {
  const redKeys = resolveRdExportRedHeaderKeys(input);
  const out: number[] = [];
  headers.forEach((h, i) => {
    if (redKeys.has(resolveRdExportHeaderKey(h))) out.push(i);
  });
  return out;
}
